import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, Zap, User } from "lucide-react";
import { Logo } from "@/components/Logo";
import { NAV_LINKS, CONTACT } from "@/lib/site";
import { useAuth } from "@/context/AuthContext";
import { scrollToSection } from "@/lib/scroll";

const go = (href) => scrollToSection(href);

function AuthButtons({ onNavigate }) {
  const { user, login } = useAuth();
  if (user) {
    return (
      <Link
        data-testid="nav-profile-link"
        to="/profil"
        onClick={onNavigate}
        className="flex items-center gap-2 rounded-full border border-neon/40 bg-neon/10 px-4 py-2.5 text-[13px] font-extrabold text-neon transition-transform duration-200 hover:scale-[1.03]"
      >
        {user.picture ? (
          <img src={user.picture} alt="" className="h-5 w-5 rounded-full" referrerPolicy="no-referrer" />
        ) : (
          <User size={15} />
        )}
        Mon espace
      </Link>
    );
  }
  return (
    <button
      data-testid="nav-login-btn"
      onClick={login}
      className="flex items-center gap-2 rounded-full border border-line px-4 py-2.5 text-[13px] font-bold text-white transition-colors duration-200 hover:border-neon hover:text-neon"
    >
      <User size={15} className="text-neon" />
      Connexion
    </button>
  );
}

export default function Nav() {
  const [open, setOpen] = useState(false);

  return (
    <header className="glass fixed inset-x-0 top-0 z-50 border-b border-line">
      <div className="mx-auto flex h-[72px] w-full max-w-7xl items-center justify-between px-5 lg:px-8">
        <button
          data-testid="nav-home-logo"
          onClick={() => window.__lenis ? window.__lenis.scrollTo(0, { duration: 1 }) : window.scrollTo({ top: 0, behavior: "smooth" })}
          className="shrink-0"
          aria-label="MAXREVIX — accueil"
        >
          <Logo />
        </button>

        <nav className="hidden items-center gap-7 lg:flex">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              onClick={() => go(l.href)}
              className="text-[13px] font-semibold uppercase tracking-wider text-slate-400 transition-colors duration-200 hover:text-neon"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            data-testid="nav-phone-btn"
            href={CONTACT.phoneHref}
            className="flex items-center gap-2 rounded-full border border-line px-4 py-2.5 text-[13px] font-bold text-white transition-colors duration-200 hover:border-neon hover:text-neon"
          >
            <Phone size={15} className="text-neon" />
            {CONTACT.phoneDisplay}
          </a>
          <AuthButtons />
          <button
            data-testid="nav-devis-btn"
            onClick={() => go("#devis")}
            className="flex items-center gap-2 rounded-full bg-neon px-5 py-2.5 text-[13px] font-extrabold uppercase tracking-wider text-night chip-glow transition-transform duration-200 hover:scale-[1.04]"
          >
            <Zap size={15} />
            Devis gratuit
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="rounded-lg border border-line p-2 lg:hidden"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="glass border-b border-line lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`nav-mobile-link-${l.href.slice(1)}`}
                  onClick={() => { setOpen(false); go(l.href); }}
                  className="rounded-lg px-3 py-3 text-left font-display text-xl font-bold uppercase tracking-tight text-white transition-colors hover:text-neon"
                >
                  {l.label}
                </button>
              ))}
              <div className="mt-3 flex flex-col gap-3 pb-2">
                <a
                  data-testid="nav-mobile-phone-btn"
                  href={CONTACT.phoneHref}
                  className="flex items-center justify-center gap-2 rounded-full border border-line px-4 py-3 text-sm font-bold text-white"
                >
                  <Phone size={16} className="text-neon" />
                  {CONTACT.phoneDisplay}
                </a>
                <AuthButtons onNavigate={() => setOpen(false)} />
                <button
                  data-testid="nav-mobile-devis-btn"
                  onClick={() => { setOpen(false); go("#devis"); }}
                  className="flex items-center justify-center gap-2 rounded-full bg-neon px-4 py-3 text-sm font-extrabold uppercase tracking-wider text-night chip-glow"
                >
                  <Zap size={16} />
                  Devis gratuit
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
